/**
 * ProductDepthBlock — Wave 173
 *
 * Deterministic (no runtime AI) depth block mounted on every Store / product
 * detail page across the 5 clusters: notary, apostille, naati, sworn, visa.
 *
 * Sections: เอกสารที่ต้องเตรียม · ขั้นตอนการทำงาน · ข้อควรระวัง ·
 * ตารางเปรียบเทียบทางเลือก · กล่องที่ปรึกษา 15+ ปี.
 *
 * Rules honoured:
 *  - No price strings (SERP + snippet safety; prices live in the product card).
 *  - No Review / AggregateRating / ClaimReview JSON-LD emitted here.
 *  - Visible content only — schema is produced by the route head() + Auto* mount.
 */
import { Link } from "@tanstack/react-router";

export type DepthCluster = "notary" | "apostille" | "naati" | "sworn" | "visa";

type Step = { title: string; detail: string; who: string; eta: string };
type Row = { option: string; useWhen: string; time: string; note: string };
type Pack = {
  documents: string[];
  steps: Step[];
  cautions: string[];
  compare: Row[];
  advisory: string;
};

const TH: Record<DepthCluster, Pack> = {
  notary: {
    documents: [
      "หนังสือเดินทางหรือบัตรประชาชนตัวจริงของผู้ลงนาม (ต้องยังไม่หมดอายุ)",
      "เอกสารต้นฉบับที่ต้องการให้ทนายรับรอง — ห้ามลงลายมือชื่อล่วงหน้า ต้องลงต่อหน้าทนาย",
      "คำแปลภาษาอังกฤษ (ถ้าเอกสารต้นฉบับเป็นภาษาไทยและปลายทางต้องการฉบับอังกฤษ)",
      "หนังสือรับรองบริษัทอายุไม่เกิน 6 เดือน + บัตรประชาชนกรรมการ กรณีลงนามในนามนิติบุคคล",
      "หนังสือมอบอำนาจ พร้อมสำเนาบัตรประชาชนผู้มอบและผู้รับมอบ กรณีมอบให้ผู้อื่นดำเนินการแทน",
      "แบบฟอร์มปลายทาง (ถ้ามี) เช่น แบบของธนาคาร มหาวิทยาลัย หรือหน่วยงานต่างประเทศ",
    ],
    steps: [
      { title: "ส่งไฟล์ให้ประเมินก่อน", detail: "ส่งภาพถ่ายหรือ PDF ทุกหน้าทาง LINE ทีมงานตรวจว่าปลายทางต้องการ notarial act แบบใด (รับรองลายมือชื่อ / รับรองสำเนา / affidavit) และมีจุดที่จะถูกตีกลับหรือไม่", who: "ทีมที่ปรึกษา", eta: "ภายในวันทำการเดียวกัน" },
      { title: "จัดเตรียมและตรวจร่างเอกสาร", detail: "ปรับถ้อยคำ ตรวจการสะกดชื่อให้ตรงกับหนังสือเดินทางทุกตัวอักษร และจัดหน้าให้มีพื้นที่สำหรับตราประทับและ notarial certificate", who: "ทีมเอกสาร", eta: "1 วันทำการ" },
      { title: "ลงนามต่อหน้าทนาย Notarial Services Attorney", detail: "ผู้ลงนามแสดงตนพร้อมเอกสารตัวจริง ลงลายมือชื่อต่อหน้าทนายที่ขึ้นทะเบียนกับสภาทนายความ ทนายบันทึกลงสมุดทะเบียน", who: "ทนายผู้ทำคำรับรอง", eta: "15–30 นาที" },
      { title: "ออกคำรับรองและประทับตรา", detail: "ทนายลงนาม ประทับตรา และแนบ notarial certificate ภาษาอังกฤษ พร้อมเย็บริบบิ้นหรือแนบชุดเอกสารตามที่ปลายทางกำหนด", who: "ทนายผู้ทำคำรับรอง", eta: "วันเดียวกัน" },
      { title: "ต่อยอดสู่กรมการกงสุล / สถานทูต (ถ้าจำเป็น)", detail: "หากปลายทางต้องการรับรองซ้อน ทีมงานเดินเรื่องต่อที่กรมการกงสุล และสถานทูตปลายทางให้จบในสายเดียว", who: "ทีมเดินเรื่อง", eta: "2–7 วันทำการ" },
    ],
    cautions: [
      "ลงลายมือชื่อมาก่อนเข้าพบทนาย — เป็นสาเหตุที่เอกสารถูกตีกลับมากที่สุด ต้องเซ็นต่อหน้าเท่านั้น",
      "ชื่อ-สกุลสะกดไม่ตรงกับหนังสือเดินทางแม้แต่ตัวอักษรเดียว ปลายทางมีสิทธิปฏิเสธทั้งชุด",
      "ทนายรับรองลายมือชื่อและข้อเท็จจริงที่ปรากฏ ไม่ได้รับรองว่าเนื้อหาในเอกสารเป็นความจริงทุกประการ",
      "บางประเทศต้องรับรองต่อที่กรมการกงสุลและสถานทูต การหยุดแค่ชั้นทนายจะใช้ไม่ได้",
      "เอกสารที่ต้องใช้หลายประเทศ ควรทำเป็นชุดแยกกัน อย่าใช้ชุดเดียวเวียนหลายปลายทาง",
    ],
    compare: [
      { option: "รับรองโดยทนาย Notary Public เท่านั้น", useWhen: "ปลายทางเป็นเอกชน เช่น ธนาคาร มหาวิทยาลัย คู่สัญญาต่างประเทศ", time: "1–2 วันทำการ", note: "เร็วที่สุด แต่หน่วยงานราชการต่างประเทศบางแห่งไม่รับ" },
      { option: "ทนาย + กรมการกงสุล (MFA)", useWhen: "ปลายทางเป็นหน่วยงานรัฐ หรือขั้นตอนถัดไปคือสถานทูต", time: "3–5 วันทำการ", note: "เป็นเส้นทางมาตรฐานของเอกสารส่วนใหญ่" },
      { option: "ทนาย + MFA + Apostille", useWhen: "ปลายทางเป็นประเทศสมาชิกอนุสัญญา Apostille", time: "5–7 วันทำการ", note: "ไทยเข้าร่วมอนุสัญญาแล้ว ตรวจสอบรูปแบบที่ปลายทางรับก่อนยื่น" },
      { option: "ทนาย + MFA + สถานทูตปลายทาง", useWhen: "ประเทศที่ยังไม่ใช่สมาชิกอนุสัญญา เช่น หลายประเทศตะวันออกกลาง จีน", time: "7–15 วันทำการ", note: "แต่ละสถานทูตมีคิวและแบบฟอร์มต่างกัน" },
    ],
    advisory: "เราไม่ได้เป็นแค่ผู้รับจ้างเดินเอกสาร ทีมที่ปรึกษาประสบการณ์มากกว่า 15 ปี จะตรวจตั้งแต่ต้นทางว่าปลายทางของคุณต้องการรับรองกี่ชั้น ต้องแปลก่อนหรือหลัง และควรทำกี่ชุด เพื่อไม่ให้เสียเวลาทำซ้ำ ส่งไฟล์มาให้ประเมินก่อนได้เลย ไม่ต้องเดินเรื่องเอง",
  },
  apostille: {
    documents: [
      "เอกสารตัวจริงที่ออกโดยหน่วยงานไทย เช่น สูติบัตร ทะเบียนสมรส ใบรับรองความประพฤติ ทรานสคริปต์",
      "คำแปลภาษาอังกฤษที่ผ่านมาตรฐานกรมการกงสุล (คำแปลผิดแบบฟอร์มคือสาเหตุตีกลับอันดับหนึ่ง)",
      "สำเนาหนังสือเดินทางหรือบัตรประชาชนของเจ้าของเอกสาร",
      "หนังสือมอบอำนาจพร้อมสำเนาบัตรประชาชน หากให้ทีมงานยื่นแทน",
      "หนังสือรับรองบริษัทฉบับล่าสุด กรณีเอกสารนิติบุคคล",
      "ข้อกำหนดเป็นลายลักษณ์อักษรจากปลายทาง (ถ้ามี) ว่าต้องการ Apostille หรือ legalization แบบใด",
    ],
    steps: [
      { title: "ตรวจว่าปลายทางรับ Apostille จริงหรือไม่", detail: "ประเทศสมาชิกอนุสัญญา Hague รับ Apostille ส่วนประเทศนอกอนุสัญญาต้องรับรองสถานทูต ทีมงานตรวจให้ก่อนเริ่ม", who: "ทีมที่ปรึกษา", eta: "ภายในวันเดียว" },
      { title: "จัดหาเอกสารต้นทางให้ถูกฉบับ", detail: "บางกรณีต้องคัดสำเนาที่รับรองโดยอำเภอ/เขต หรือขอฉบับภาษาอังกฤษจากหน่วยงานผู้ออกก่อน", who: "ทีมเดินเรื่อง", eta: "1–3 วันทำการ" },
      { title: "แปลตามมาตรฐานกรมการกงสุล", detail: "ใช้รูปแบบคำแปลและคำศัพท์ที่กองสัญชาติและนิติกรณ์ยอมรับ ตรวจการทับศัพท์ชื่อให้ตรงหนังสือเดินทาง", who: "นักแปลเอกสารราชการ", eta: "1–2 วันทำการ" },
      { title: "ยื่นรับรองที่กรมการกงสุล", detail: "ยื่นแบบปกติหรือแบบด่วนตามความเร่งด่วนของลูกค้า พร้อมติดตามผลและแก้ไขทันทีหากเจ้าหน้าที่ขอปรับคำแปล", who: "ทีมเดินเรื่อง", eta: "2–5 วันทำการ" },
      { title: "ส่งมอบและแนะนำการใช้งานปลายทาง", detail: "อธิบายว่าปลายทางต้องใช้ต้นฉบับหรือสำเนา ต้องยื่นภายในกี่เดือน และควรเก็บชุดสำรองกี่ชุด", who: "ทีมที่ปรึกษา", eta: "วันส่งมอบ" },
    ],
    cautions: [
      "เอกสารเก่าเกินกำหนดที่ปลายทางรับ (หลายแห่งรับไม่เกิน 3–6 เดือน) ต้องคัดใหม่ก่อนรับรอง",
      "คำแปลที่ทำเองหรือแปลด้วยเครื่องมักถูกตีกลับ เพราะรูปแบบไม่ตรงมาตรฐานกองสัญชาติและนิติกรณ์",
      "ชื่อสะกดในคำแปลต้องตรงกับหนังสือเดินทาง ไม่ใช่ตรงกับคำอ่านที่คุ้นเคย",
      "Apostille รับรองความถูกต้องของตราและลายมือชื่อเจ้าหน้าที่ ไม่ได้รับรองเนื้อหาในเอกสาร",
      "อย่ายิงงานด่วนวันศุกร์เย็นหรือช่วงวันหยุดยาว คิวราชการจะทบไปสัปดาห์ถัดไป",
    ],
    compare: [
      { option: "Apostille ปกติ", useWhen: "ปลายทางเป็นประเทศสมาชิกอนุสัญญาและไม่เร่ง", time: "3–5 วันทำการ", note: "ต้นทุนต่ำที่สุด วางแผนล่วงหน้าได้" },
      { option: "Apostille แบบด่วน", useWhen: "มีกำหนดยื่นวีซ่าหรือปิดดีลภายในสัปดาห์นี้", time: "1–2 วันทำการ", note: "คิวด่วนมีจำนวนจำกัดต่อวัน" },
      { option: "รับรองกงสุล + สถานทูตปลายทาง", useWhen: "ประเทศนอกอนุสัญญา", time: "7–15 วันทำการ", note: "ขึ้นกับคิวและข้อกำหนดของแต่ละสถานทูต" },
      { option: "รับรองโดยทนาย Notary อย่างเดียว", useWhen: "ปลายทางเป็นเอกชนที่ไม่ต้องการรับรองราชการ", time: "1–2 วันทำการ", note: "ใช้กับหน่วยงานรัฐต่างประเทศไม่ได้" },
    ],
    advisory: "ลูกค้าจำนวนมากเสียเวลาเพราะเลือกเส้นทางรับรองผิดตั้งแต่ต้น ทีมที่ปรึกษาของเราซึ่งดูแลงานรับรองเอกสารมากว่า 15 ปี จะวางลำดับให้ครบว่าแปลก่อนหรือรับรองก่อน ยื่นกี่ชั้น และควรเผื่อเวลากี่วันก่อนวันนัดที่สถานทูต ถ้าไม่อยากยุ่งยาก ให้เราดูแลตั้งแต่คัดเอกสารจนถึงส่งถึงมือ",
  },
  naati: {
    documents: [
      "ไฟล์สแกนสีคมชัดของเอกสารต้นฉบับทุกหน้า รวมหน้าตราประทับและหน้าหลัง",
      "หน้าหนังสือเดินทาง เพื่อยึดการสะกดชื่อให้ตรงกันทุกฉบับ",
      "รายชื่อเอกสารและหน่วยงานปลายทาง เช่น DHA, VETASSESS, AITSL, Engineers Australia, มหาวิทยาลัย",
      "แบบฟอร์มหรือ checklist ที่ปลายทางส่งมา (ถ้ามี) เพื่อจับคู่ชื่อเอกสารให้ตรง",
      "คำสะกดชื่อเฉพาะ เช่น ชื่อโรงเรียน บริษัท ตำแหน่งงาน ที่ต้องการให้ใช้เป็นทางการ",
    ],
    steps: [
      { title: "ตรวจไฟล์และประเมินจำนวนหน้า", detail: "ทีมงานตรวจความคมชัด ความครบถ้วนของตราประทับ และยืนยันว่าปลายทางต้องการ NAATI certified translation จริง", who: "ทีมที่ปรึกษา", eta: "ภายในวันเดียวกัน" },
      { title: "จ่ายงานให้นักแปล NAATI ที่ตรงสาขา", detail: "เลือกนักแปลที่ถือ certification คู่ภาษาไทย–อังกฤษ และคุ้นเคยกับประเภทเอกสาร เช่น การศึกษา ราชการ การแพทย์", who: "นักแปล NAATI", eta: "1–3 วันทำการ" },
      { title: "ตรวจทานสองชั้นและจัดรูปแบบ", detail: "ตรวจตัวเลข วันที่ ชื่อเฉพาะ และจัดรูปแบบให้สะท้อนต้นฉบับ พร้อมระบุตราประทับและลายเซ็นที่ปรากฏ", who: "ผู้ตรวจทาน", eta: "1 วันทำการ" },
      { title: "ประทับตรา NAATI และออกไฟล์รับรอง", detail: "แนบตรา NAATI หมายเลขผู้แปลและวันที่ ส่งเป็น PDF ที่ยื่นออนไลน์ได้ พร้อมฉบับพิมพ์เมื่อจำเป็น", who: "นักแปล NAATI", eta: "วันเดียวกัน" },
      { title: "แนะนำวิธียื่นและเก็บสำรอง", detail: "อธิบายว่าปลายทางยอมรับ PDF หรือขอฉบับจริง และควรเก็บชุดสำรองกี่ชุดสำหรับการยื่นรอบถัดไป", who: "ทีมที่ปรึกษา", eta: "วันส่งมอบ" },
    ],
    cautions: [
      "สแกนมืดหรือถ่ายเอียงจนอ่านตราประทับไม่ออก ทำให้คำแปลระบุข้อมูลไม่ครบและถูกปลายทางทัก",
      "สะกดชื่อไม่ตรงหนังสือเดินทาง เป็นเหตุให้เอกสารชุดเดียวกันถูกมองว่าเป็นคนละคน",
      "แปลไม่ครบหน้า เช่น ข้ามหน้าหลังที่มีตราประทับ ปลายทางถือว่าเอกสารไม่สมบูรณ์",
      "บางหน่วยงานต้องการต้นฉบับรับรองเพิ่ม ไม่ใช่แค่คำแปล ควรตรวจข้อกำหนดก่อนสั่งงาน",
      "อย่าใช้คำแปลเครื่องมาแก้เอง เพราะคำศัพท์ราชการและชื่อวุฒิการศึกษาต้องใช้รูปแบบเฉพาะ",
    ],
    compare: [
      { option: "NAATI certified translation", useWhen: "ยื่นออสเตรเลีย เช่น วีซ่า DHA, ประเมินวุฒิ, สภาวิชาชีพ", time: "1–3 วันทำการ", note: "มาตรฐานที่หน่วยงานออสเตรเลียยอมรับกว้างที่สุด" },
      { option: "แปลรับรองโดยผู้แปลที่ขึ้นทะเบียน (สำหรับกงสุล)", useWhen: "ต้องยื่นรับรองที่กรมการกงสุลหรือสถานทูตในไทย", time: "1–2 วันทำการ", note: "คนละมาตรฐานกับ NAATI ใช้แทนกันไม่ได้เสมอไป" },
      { option: "แปลทั่วไปไม่รับรอง", useWhen: "ใช้อ่านภายในองค์กรหรือประกอบการตัดสินใจ", time: "1 วันทำการ", note: "ยื่นราชการไม่ได้" },
      { option: "NAATI + รับรองเพิ่มที่ต้นทาง", useWhen: "ปลายทางขอทั้งคำแปลและเอกสารต้นทางที่รับรองแล้ว", time: "3–7 วันทำการ", note: "ควรทำคู่ขนานเพื่อประหยัดเวลา" },
    ],
    advisory: "ทีมงานของเราไม่ได้แปลแล้วจบ เราให้คำปรึกษาด้วยประสบการณ์กว่า 15 ปี ว่าเอกสารชุดไหนต้อง NAATI ชุดไหนต้องรับรองกงสุล และควรยื่นลำดับใดเพื่อไม่ให้เสียค่าใช้จ่ายซ้ำ ส่งไฟล์มาให้ประเมินฟรีก่อนตัดสินใจได้เลย",
  },
  sworn: {
    documents: [
      "ต้นฉบับหรือสแกนสีของเอกสารที่ต้องการแปลรับรอง ครบทุกหน้า",
      "สำเนาหนังสือเดินทางของเจ้าของเอกสาร เพื่อยึดการสะกดชื่อ",
      "ชื่อหน่วยงานปลายทางและประเทศ เพื่อเลือกรูปแบบคำรับรองให้ตรง",
      "แบบฟอร์มหรือคู่มือของปลายทาง (ถ้ามี) ที่ระบุว่าต้องการผู้แปลประเภทใด",
      "หนังสือรับรองบริษัท กรณีเอกสารนิติบุคคลที่ต้องรับรองผู้มีอำนาจลงนาม",
    ],
    steps: [
      { title: "ประเมินว่าปลายทางต้องการผู้แปลแบบใด", detail: "sworn translator, certified translation, หรือแปลพร้อมรับรองกงสุล — แต่ละแบบมีผลต่างกัน ทีมงานตรวจให้ก่อนเริ่มงาน", who: "ทีมที่ปรึกษา", eta: "ภายในวันเดียวกัน" },
      { title: "แปลโดยผู้แปลสาขาที่ตรงกับเนื้อหา", detail: "งานกฎหมาย การแพทย์ วิศวกรรม และการเงินใช้คำศัพท์เฉพาะ จ่ายงานตามความเชี่ยวชาญเพื่อลดการตีความผิด", who: "ผู้แปลผู้เชี่ยวชาญ", eta: "1–3 วันทำการ" },
      { title: "ตรวจทานและออกคำรับรอง", detail: "ตรวจตัวเลข วันที่ ชื่อเฉพาะ แล้วออกคำรับรองพร้อมลายมือชื่อผู้แปลและข้อมูลติดต่อที่ปลายทางตรวจสอบได้", who: "ผู้ตรวจทาน", eta: "1 วันทำการ" },
      { title: "ต่อยอดรับรองตามที่ปลายทางกำหนด", detail: "หากต้องรับรองต่อโดยทนาย กรมการกงสุล หรือสถานทูต ทีมงานเดินเรื่องต่อให้ในสายเดียวไม่ต้องเริ่มใหม่", who: "ทีมเดินเรื่อง", eta: "2–10 วันทำการ" },
    ],
    cautions: [
      "คำว่า sworn translator มีนิยามต่างกันในแต่ละประเทศ ต้องอ่านข้อกำหนดปลายทางก่อนสั่งงาน",
      "ชื่อ สถานที่ และวุฒิการศึกษา ต้องทับศัพท์ให้ตรงกับเอกสารเดิมของผู้ยื่นทุกฉบับ",
      "เอกสารที่มีตราประทับหรือลายเซ็นต้องระบุไว้ในคำแปล ไม่ใช่ปล่อยว่าง",
      "แปลก่อนแต่ยังไม่รับรองต้นฉบับ อาจต้องแปลใหม่หลังต้นฉบับถูกแก้ ควรวางลำดับให้ถูกตั้งแต่แรก",
    ],
    compare: [
      { option: "แปลรับรองโดยผู้แปลผู้เชี่ยวชาญ", useWhen: "ยื่นหน่วยงานที่รับคำรับรองของผู้แปล", time: "1–3 วันทำการ", note: "ยืดหยุ่นและเร็วที่สุดในกลุ่มงานรับรอง" },
      { option: "แปล + รับรองโดยทนาย Notary", useWhen: "ปลายทางขอให้มีผู้รับรองที่มีใบอนุญาตกำกับ", time: "2–4 วันทำการ", note: "นิยมกับธนาคารและมหาวิทยาลัยต่างประเทศ" },
      { option: "แปล + รับรองกรมการกงสุล", useWhen: "ยื่นหน่วยงานราชการต่างประเทศหรือสถานทูต", time: "4–7 วันทำการ", note: "ต้องใช้รูปแบบคำแปลตามมาตรฐานกงสุล" },
      { option: "NAATI certified", useWhen: "ปลายทางออสเตรเลียโดยเฉพาะ", time: "1–3 วันทำการ", note: "ใช้แทนคำแปลรับรองทั่วไปในไทยไม่ได้เสมอไป" },
    ],
    advisory: "เราเป็นที่ปรึกษาด้านเอกสารข้ามประเทศมากกว่า 15 ปี ไม่ใช่แค่ผู้รับแปล เราจะบอกตรง ๆ ว่าเอกสารของคุณควรแปลแบบใดจึงจะไม่ถูกตีกลับ และถ้าต้องรับรองต่อหลายชั้น เราจัดการต่อให้จบในที่เดียว",
  },
  visa: {
    documents: [
      "หนังสือเดินทางอายุเหลือไม่น้อยกว่า 6 เดือน พร้อมหน้าวีซ่าเก่าทั้งหมด",
      "หลักฐานการเงินย้อนหลัง 3–6 เดือน และหนังสือรับรองยอดจากธนาคาร",
      "หนังสือรับรองการทำงาน หรือหนังสือรับรองบริษัทกรณีเจ้าของกิจการ",
      "เอกสารสถานะครอบครัว เช่น ทะเบียนสมรส สูติบัตร (พร้อมคำแปลรับรองเมื่อจำเป็น)",
      "แผนการเดินทาง ที่พัก และหนังสือเชิญจากปลายทาง (ถ้ามี)",
      "รูปถ่ายตามสเปกของแต่ละสถานทูต ซึ่งขนาดและพื้นหลังไม่เหมือนกันทุกประเทศ",
    ],
    steps: [
      { title: "ประเมินโปรไฟล์และความเสี่ยงก่อนยื่น", detail: "ตรวจประวัติการเดินทาง ประวัติการถูกปฏิเสธ ความมั่นคงทางการเงินและงาน เพื่อวางแนวทางยื่นที่มีโอกาสผ่านสูงที่สุด", who: "ที่ปรึกษาวีซ่า", eta: "1 วันทำการ" },
      { title: "วางเช็กลิสต์เอกสารเฉพาะเคส", detail: "ระบุว่าเอกสารใดต้องแปล เอกสารใดต้องรับรอง และเอกสารใดต้องออกใหม่ให้ทันกรอบเวลาที่สถานทูตกำหนด", who: "ทีมเอกสาร", eta: "1–2 วันทำการ" },
      { title: "จัดเตรียมและตรวจชุดเอกสาร", detail: "ตรวจความสอดคล้องของตัวเลข วันที่ และชื่อในทุกฉบับ พร้อมเรียงลำดับตามที่เจ้าหน้าที่ตรวจจริง", who: "ทีมเอกสาร", eta: "2–5 วันทำการ" },
      { title: "กรอกแบบฟอร์มและจองคิวยื่น", detail: "กรอกแบบฟอร์มออนไลน์ ชำระค่าธรรมเนียม และจองคิวศูนย์รับคำร้องหรือสถานทูตตามวันเดินทางจริง", who: "ทีมยื่นวีซ่า", eta: "ตามคิวสถานทูต" },
      { title: "ซ้อมสัมภาษณ์และติดตามผล", detail: "เตรียมคำตอบให้สอดคล้องกับเอกสาร ติดตามสถานะ และวางแผนรอบถัดไปทันทีหากผลไม่เป็นไปตามคาด", who: "ที่ปรึกษาวีซ่า", eta: "ตลอดกระบวนการ" },
    ],
    cautions: [
      "เอกสารการเงินที่มีเงินก้อนเข้ามากะทันหันก่อนยื่น มักถูกตั้งคำถามเรื่องที่มาของเงิน",
      "ข้อมูลในแบบฟอร์มไม่ตรงกับเอกสารแนบแม้จุดเล็ก อาจถูกมองว่าให้ข้อมูลคลาดเคลื่อน",
      "จองตั๋วและที่พักแบบชำระเต็มก่อนได้ผลวีซ่า เป็นความเสี่ยงที่ไม่จำเป็น",
      "คำแปลเอกสารราชการที่ไม่ได้รับรอง หลายสถานทูตไม่รับพิจารณา",
      "ยื่นกระชั้นชิดกับวันเดินทาง ทำให้ไม่มีเวลาแก้ไขเมื่อเจ้าหน้าที่ขอเอกสารเพิ่ม",
    ],
    compare: [
      { option: "ยื่นเองทั้งหมด", useWhen: "เคยได้วีซ่าประเทศนั้นมาแล้วและโปรไฟล์ไม่ซับซ้อน", time: "ขึ้นกับผู้ยื่น", note: "ประหยัดที่สุด แต่ความเสี่ยงผิดพลาดสูงหากข้อกำหนดเปลี่ยน" },
      { option: "ให้เราตรวจเอกสารก่อนยื่น", useWhen: "อยากยื่นเองแต่ต้องการความมั่นใจ", time: "1–2 วันทำการ", note: "ลดจุดผิดพลาดที่พบบ่อยได้มาก" },
      { option: "บริการเต็มรูปแบบ", useWhen: "ไม่มีเวลา หรือเคยถูกปฏิเสธมาก่อน", time: "ตามคิวสถานทูต", note: "ดูแลตั้งแต่วางกลยุทธ์ แปล รับรอง จนถึงวันยื่น" },
      { option: "ยื่นใหม่หลังถูกปฏิเสธ", useWhen: "มีหนังสือปฏิเสธและต้องการแก้ประเด็นให้ตรงจุด", time: "2–4 สัปดาห์", note: "ต้องวิเคราะห์เหตุผลปฏิเสธก่อน ห้ามยื่นซ้ำแบบเดิม" },
    ],
    advisory: "งานวีซ่าไม่ได้ชนะกันที่จำนวนเอกสาร แต่ชนะที่การเล่าเรื่องให้สอดคล้องกันทั้งชุด ทีมที่ปรึกษาประสบการณ์กว่า 15 ปี ของเราจะวางกลยุทธ์ให้เหมาะกับโปรไฟล์คุณจริง ๆ ถ้าไม่อยากเสี่ยงและไม่อยากยุ่งยาก ให้เราดูแลให้ครบตั้งแต่ต้นจนจบ",
  },
};

const EN: Record<DepthCluster, Pack> = {
  notary: {
    documents: [
      "Valid original passport or Thai ID card of every signatory",
      "The original document to be notarised — do not sign in advance; sign before the attorney",
      "English translation when the source document is in Thai and the receiving party needs English",
      "Company affidavit issued within 6 months plus director ID for corporate signing",
      "Power of attorney with ID copies if someone else attends on your behalf",
      "Any destination-specific form supplied by the bank, university or overseas authority",
    ],
    steps: [
      { title: "Send the files for a free review", detail: "Share photos or a PDF of every page on LINE. We confirm which notarial act the receiving party actually needs and flag anything that would be rejected.", who: "Advisory team", eta: "Same business day" },
      { title: "Prepare and proof the wording", detail: "We align name spelling with the passport letter by letter and lay out space for the seal and notarial certificate.", who: "Documentation team", eta: "1 business day" },
      { title: "Sign before the Notarial Services Attorney", detail: "The signatory attends with original ID, signs in the attorney's presence, and the act is recorded in the notarial register.", who: "Notarial Services Attorney", eta: "15–30 minutes" },
      { title: "Certificate issued and sealed", detail: "The attorney signs, seals and attaches an English notarial certificate, ribbon-bound as the destination requires.", who: "Notarial Services Attorney", eta: "Same day" },
      { title: "Onward MFA / embassy legalisation", detail: "If a further layer is required we file at the Department of Consular Affairs and the destination embassy in one continuous chain.", who: "Filing team", eta: "2–7 business days" },
    ],
    cautions: [
      "Signing before meeting the attorney is the single most common rejection cause",
      "One character of name mismatch against the passport can void the whole set",
      "A notary certifies signatures and observed facts, not that the content is true",
      "Some destinations require MFA and embassy layers; the notary stage alone is not enough",
      "Prepare separate sets per destination instead of circulating one original",
    ],
    compare: [
      { option: "Notary only", useWhen: "Private recipients: banks, universities, counterparties", time: "1–2 business days", note: "Fastest, but some foreign government bodies will not accept it" },
      { option: "Notary + MFA", useWhen: "Government recipients, or embassy is the next step", time: "3–5 business days", note: "The standard route for most documents" },
      { option: "Notary + MFA + Apostille", useWhen: "Destination is a Hague Convention member", time: "5–7 business days", note: "Confirm the exact format the recipient accepts first" },
      { option: "Notary + MFA + embassy", useWhen: "Non-member states such as China and several Gulf countries", time: "7–15 business days", note: "Each embassy has its own queue and forms" },
    ],
    advisory: "We are not only a filing agency. Our advisers, 15+ years in cross-border documentation, map how many certification layers your destination needs, whether translation comes before or after, and how many sets to produce — so nothing has to be redone. Send the files and let us run the whole chain.",
  },
  apostille: {
    documents: [
      "Original Thai-issued records: birth certificate, marriage certificate, police clearance, transcripts",
      "English translation in the format the Department of Consular Affairs accepts",
      "Passport or ID copy of the document holder",
      "Power of attorney with ID copy if we file on your behalf",
      "Current company affidavit for corporate documents",
      "Written requirements from the recipient stating Apostille or legalisation",
    ],
    steps: [
      { title: "Confirm the destination accepts Apostille", detail: "Hague members accept Apostille; non-members need embassy legalisation. We verify before any work starts.", who: "Advisory team", eta: "Same day" },
      { title: "Obtain the correct source copy", detail: "Some cases need a district-office certified copy or an English-language original from the issuing authority first.", who: "Filing team", eta: "1–3 business days" },
      { title: "Translate to consular standard", detail: "We use the terminology and layout the Legalisation Division accepts and match transliterated names to the passport.", who: "Government-document translator", eta: "1–2 business days" },
      { title: "File at the Department of Consular Affairs", detail: "Standard or express lane depending on your deadline, with same-day amendments if an officer requests wording changes.", who: "Filing team", eta: "2–5 business days" },
      { title: "Hand over with usage guidance", detail: "We explain whether the recipient needs originals, the validity window, and how many spare sets to keep.", who: "Advisory team", eta: "On delivery" },
    ],
    cautions: [
      "Records older than the recipient's window (often 3–6 months) must be reissued",
      "Self-made or machine translations are commonly rejected for format reasons",
      "Name spelling must match the passport, not the familiar pronunciation",
      "Apostille verifies the seal and signature, not the content of the document",
      "Avoid launching urgent jobs late Friday or before long public holidays",
    ],
    compare: [
      { option: "Standard Apostille", useWhen: "Hague member destination, no deadline pressure", time: "3–5 business days", note: "Lowest cost, easy to plan" },
      { option: "Express Apostille", useWhen: "Visa appointment or deal closing this week", time: "1–2 business days", note: "Daily express slots are limited" },
      { option: "MFA + embassy legalisation", useWhen: "Non-Hague destination", time: "7–15 business days", note: "Depends on each embassy's queue" },
      { option: "Notary only", useWhen: "Private recipient with no government requirement", time: "1–2 business days", note: "Not valid for foreign government bodies" },
    ],
    advisory: "Most delays come from choosing the wrong certification route on day one. With 15+ years handling legalisation chains, our advisers set the correct order — translate first or certify first, how many layers, and how much buffer before your embassy appointment. Prefer not to deal with it? We handle it end to end.",
  },
  naati: {
    documents: [
      "Sharp colour scans of every page, including stamps and reverse sides",
      "Passport page so name spelling stays consistent across the set",
      "List of documents and the receiving body: DHA, VETASSESS, AITSL, Engineers Australia, universities",
      "Any checklist the recipient issued so document names can be matched exactly",
      "Preferred official spellings for schools, employers and job titles",
    ],
    steps: [
      { title: "Review files and count pages", detail: "We check legibility, confirm stamps are captured, and verify the recipient truly requires NAATI certified translation.", who: "Advisory team", eta: "Same business day" },
      { title: "Assign a subject-matched NAATI translator", detail: "A certified Thai–English translator experienced in your document type — education, civil registry or medical.", who: "NAATI translator", eta: "1–3 business days" },
      { title: "Two-stage proofing and formatting", detail: "Numbers, dates and proper nouns are verified and the layout mirrors the source, with all seals and signatures described.", who: "Reviewer", eta: "1 business day" },
      { title: "NAATI stamp and certified file", detail: "Stamp, practitioner number and date applied; delivered as an upload-ready PDF plus printed copies where needed.", who: "NAATI translator", eta: "Same day" },
      { title: "Submission guidance", detail: "We advise whether PDFs are accepted, and how many spare sets to keep for the next lodgement.", who: "Advisory team", eta: "On delivery" },
    ],
    cautions: [
      "Dark or skewed scans hide seal text, leaving gaps the recipient will query",
      "Name spelling that differs from the passport makes one applicant look like two",
      "Skipping reverse pages with stamps makes the set incomplete",
      "Some bodies want certified originals as well as translations — check first",
      "Never patch a machine translation; official titles need fixed terminology",
    ],
    compare: [
      { option: "NAATI certified translation", useWhen: "Australian lodgements: DHA visas, skills assessment, professional bodies", time: "1–3 business days", note: "Widest acceptance across Australian authorities" },
      { option: "Registered-translator certification (for consular use)", useWhen: "Filing at the Thai MFA or an embassy in Thailand", time: "1–2 business days", note: "A different standard; not always interchangeable with NAATI" },
      { option: "Non-certified translation", useWhen: "Internal reading or decision support", time: "1 business day", note: "Cannot be lodged with authorities" },
      { option: "NAATI plus source-side certification", useWhen: "Recipient wants both the translation and certified source records", time: "3–7 business days", note: "Run both tracks in parallel to save time" },
    ],
    advisory: "We do not stop at translating. With 15+ years of case experience we tell you which documents genuinely need NAATI, which need consular certification, and in what order to lodge so nothing is paid for twice. Send your files for a free assessment first.",
  },
  sworn: {
    documents: [
      "Originals or colour scans of every page to be translated",
      "Passport copy of the document holder to fix name spelling",
      "Receiving authority and country so the certification wording matches",
      "Any recipient guideline stating the translator type required",
      "Company affidavit for corporate documents requiring signatory verification",
    ],
    steps: [
      { title: "Determine the translator standard required", detail: "Sworn translator, certified translation, or translation plus consular legalisation each carry different weight. We confirm before starting.", who: "Advisory team", eta: "Same business day" },
      { title: "Assign a subject-specialist translator", detail: "Legal, medical, engineering and financial texts use fixed terminology; matching the specialism avoids misinterpretation.", who: "Specialist translator", eta: "1–3 business days" },
      { title: "Proof and issue the certification", detail: "Figures, dates and proper nouns are verified, then a signed certification with verifiable contact details is attached.", who: "Reviewer", eta: "1 business day" },
      { title: "Onward certification as required", detail: "If a notary, the MFA or an embassy layer is needed, we continue the same chain instead of restarting.", who: "Filing team", eta: "2–10 business days" },
    ],
    cautions: [
      "\"Sworn translator\" is defined differently by country — read the recipient's rule first",
      "Names, places and qualifications must be transliterated consistently across the set",
      "Seals and signatures must be described in the translation, never left blank",
      "Translating before the source is finalised often means paying to translate twice",
    ],
    compare: [
      { option: "Certified specialist translation", useWhen: "Recipient accepts a translator's certification", time: "1–3 business days", note: "Fastest and most flexible certified option" },
      { option: "Translation + notary certification", useWhen: "Recipient wants a licensed professional attesting the set", time: "2–4 business days", note: "Common for foreign banks and universities" },
      { option: "Translation + MFA legalisation", useWhen: "Foreign government bodies or embassies", time: "4–7 business days", note: "Requires consular-standard translation format" },
      { option: "NAATI certified", useWhen: "Australia-specific lodgements", time: "1–3 business days", note: "Not always a substitute for Thai certified translation" },
    ],
    advisory: "We act as advisers, not just translators. With 15+ years across cross-border filings we will tell you plainly which certification your document needs to pass first time, and if several layers are required we complete them all in one place.",
  },
  visa: {
    documents: [
      "Passport valid at least 6 months, with all previous visa pages",
      "3–6 months of bank statements plus a bank certification letter",
      "Employment letter, or company affidavit for business owners",
      "Civil status records — marriage certificate, birth certificates — with certified translations where required",
      "Itinerary, accommodation and invitation letter where applicable",
      "Photos to each embassy's own size and background specification",
    ],
    steps: [
      { title: "Profile and risk assessment", detail: "Travel history, prior refusals, financial and employment stability are reviewed to shape the strongest lodgement approach.", who: "Visa adviser", eta: "1 business day" },
      { title: "Case-specific document checklist", detail: "We identify what must be translated, what must be certified, and what has to be reissued to stay inside the embassy's validity window.", who: "Documentation team", eta: "1–2 business days" },
      { title: "Assemble and cross-check the set", detail: "Figures, dates and names are reconciled across every document and ordered the way officers actually review them.", who: "Documentation team", eta: "2–5 business days" },
      { title: "Forms, fees and appointment", detail: "Online forms completed, fees paid, and the visa centre appointment booked around your real travel dates.", who: "Lodgement team", eta: "Per embassy queue" },
      { title: "Interview prep and follow-up", detail: "Answers are aligned with the evidence, status is tracked, and a next-round plan is ready if the outcome is not as expected.", who: "Visa adviser", eta: "Throughout" },
    ],
    cautions: [
      "Large deposits appearing shortly before lodgement invite source-of-funds questions",
      "Any mismatch between the form and the attachments can be read as misrepresentation",
      "Paying for non-refundable tickets before approval is an avoidable risk",
      "Uncertified translations of civil records are refused by many embassies",
      "Lodging close to departure leaves no room to supply requested extra evidence",
    ],
    compare: [
      { option: "Self-lodgement", useWhen: "Previous visa for that country and a simple profile", time: "Applicant-dependent", note: "Cheapest, but rules change often" },
      { option: "Document review before lodging", useWhen: "You want to file yourself with a safety check", time: "1–2 business days", note: "Removes the most frequent errors" },
      { option: "Full-service handling", useWhen: "No time, or a previous refusal", time: "Per embassy queue", note: "Strategy, translation, certification and lodgement in one chain" },
      { option: "Re-application after refusal", useWhen: "You hold a refusal letter and need the issue addressed", time: "2–4 weeks", note: "Analyse the grounds first; never refile the same pack" },
    ],
    advisory: "Visa outcomes are not won by document volume but by a consistent story across the whole set. Our advisers, 15+ years in the field, build the approach around your actual profile. If you would rather not carry the risk, we run it end to end for you.",
  },
};

const LABELS = {
  th: {
    documents: "เอกสารเบื้องต้นที่ต้องเตรียม",
    steps: "ขั้นตอนการทำงานจริง",
    who: "ผู้รับผิดชอบ",
    eta: "ระยะเวลา",
    cautions: "ข้อควรระวังที่ทำให้งานถูกตีกลับ",
    compare: "เปรียบเทียบทางเลือก",
    option: "ทางเลือก",
    useWhen: "ใช้เมื่อ",
    time: "ระยะเวลา",
    note: "หมายเหตุ",
    advisory: "ที่ปรึกษา ไม่ใช่แค่ผู้รับทำ",
    cta: "ส่งเอกสารให้เราประเมินทาง LINE",
    contact: "ติดต่อทีมที่ปรึกษา",
  },
  en: {
    documents: "Documents to prepare",
    steps: "How the work actually runs",
    who: "Handled by",
    eta: "Time",
    cautions: "Why cases get rejected",
    compare: "Compare your options",
    option: "Option",
    useWhen: "Use when",
    time: "Time",
    note: "Note",
    advisory: "Advisers, not just a filing agency",
    cta: "Send your documents on LINE for a review",
    contact: "Talk to an adviser",
  },
} as const;

export default function ProductDepthBlock({
  cluster,
  locale,
}: {
  cluster: DepthCluster;
  locale: "th" | "en";
}) {
  const isTh = locale === "th";
  const pack = (isTh ? TH : EN)[cluster];
  const t = isTh ? LABELS.th : LABELS.en;
  const showContact = !isTh;

  return (
    <section className="mx-auto max-w-6xl px-4 pb-14" data-depth-cluster={cluster}>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="font-display text-xl font-extrabold text-navy-deep">{t.documents}</h2>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-slate-700">
            {pack.documents.map((d) => <li key={d}>{d}</li>)}
          </ul>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="font-display text-xl font-extrabold text-navy-deep">{t.cautions}</h2>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-slate-700">
            {pack.cautions.map((c) => <li key={c}>{c}</li>)}
          </ul>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="font-display text-xl font-extrabold text-navy-deep">{t.steps}</h2>
        <ol className="mt-3 space-y-3">
          {pack.steps.map((s, i) => (
            <li key={s.title} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <div className="font-bold text-navy-deep">{i + 1}. {s.title}</div>
              <p className="mt-1 text-sm text-slate-700">{s.detail}</p>
              <p className="mt-2 text-xs text-slate-500">{t.who}: {s.who} · {t.eta}: {s.eta}</p>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="font-display text-xl font-extrabold text-navy-deep">{t.compare}</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-300 text-left text-navy-deep">
                <th className="py-2 pr-4">{t.option}</th>
                <th className="py-2 pr-4">{t.useWhen}</th>
                <th className="py-2 pr-4">{t.time}</th>
                <th className="py-2">{t.note}</th>
              </tr>
            </thead>
            <tbody>
              {pack.compare.map((r) => (
                <tr key={r.option} className="border-b border-slate-200 align-top">
                  <td className="py-2 pr-4 font-semibold text-navy-deep">{r.option}</td>
                  <td className="py-2 pr-4 text-slate-700">{r.useWhen}</td>
                  <td className="py-2 pr-4 text-slate-700">{r.time}</td>
                  <td className="py-2 text-slate-700">{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-gold/40 bg-navy-deep/[0.03] p-5">
        <h2 className="font-display text-xl font-extrabold text-navy-deep">{t.advisory}</h2>
        <p className="mt-2 text-slate-800" data-speakable="true">{pack.advisory}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <a
            href="https://lin.ee/T6DnVXC"
            rel="noopener"
            className="inline-flex min-h-11 items-center rounded-xl bg-gold px-5 py-2 font-bold text-navy-deep"
          >
            {t.cta}
          </a>
          {showContact ? (
            <Link
              to="/en/contact"
              className="inline-flex min-h-11 items-center rounded-xl border border-navy-deep/20 px-5 py-2 font-semibold text-navy-deep"
            >
              {t.contact}
            </Link>
          ) : null}
        </div>
      </div>
    </section>
  );
}
